import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { ArrowUpRight, Droplets, HardHat, ShieldCheck, CheckCircle2, Factory } from "lucide-react";
import projWater from "@/assets/project-water.jpg";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

function Reveal({ children, delay = 0, direction = "up", className = "" }: { children: React.ReactNode; delay?: number; direction?: "up" | "left" | "right"; className?: string }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-8%" });
    const hidden = direction === "up" ? { opacity: 0, y: 40 } : direction === "left" ? { opacity: 0, x: 50 } : { opacity: 0, x: -50 };
    const visible = direction === "up" ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 };
    return (
        <motion.div ref={ref} className={className} initial="hidden" animate={inView ? "visible" : "hidden"}
            variants={{ hidden, visible: { ...visible, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } } }}>
            {children}
        </motion.div>
    );
}

export default function ForageEau() {
    const { t } = useTranslation("services");
    const s = (k: string) => t(`water.${k}`);
    const types = t("water.types", { returnObjects: true }) as { title: string; items: string[] }[];
    const stats = t("water.stats", { returnObjects: true }) as { n: string; u: string; label: string }[];
    const equipment = t("water.equipment", { returnObjects: true }) as { title: string; desc: string }[];
    return (
        <>
            <Helmet>
                <title>{s("meta_title")}</title>
                <meta name="description" content={s("meta_desc")} />
                <meta name="keywords" content={s("meta_keywords")} />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content={s("meta_title")} />
                <meta property="og:description" content={s("meta_desc")} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://salmamydrill.com/forage-eau" />
                <meta property="og:image" content={projWater} />
                <meta property="og:locale" content="fr_FR" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={s("meta_title")} />
                <meta name="twitter:description" content={s("meta_desc")} />
                <meta name="twitter:image" content={projWater} />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600&display=swap" />
            </Helmet>
            <div className="min-h-screen bg-background text-foreground flex flex-col overflow-x-hidden">
                <Nav />

                {/* Dark Premium Hero */}
                <section className="relative bg-surface text-surface-foreground pt-32 pb-24 md:pt-48 md:pb-32 px-6 grain overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-surface via-surface to-background/5" />
                    <motion.div initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 0.3, scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }} className="absolute inset-0 lg:left-1/3 pointer-events-none mix-blend-screen">
                        <img src={projWater} alt="Forage Hydraulique" className="w-full h-full object-cover mask-image-l" />
                    </motion.div>

                    <div className="relative z-10 mx-auto max-w-7xl flex flex-col items-start">
                        <motion.span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}>
                            <span className="w-12 h-px bg-primary/40 block"></span>
                            {s("eyebrow")}
                        </motion.span>
                        <div className="overflow-hidden mb-4">
                            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}>
                                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-black leading-[1.1] tracking-tighter uppercase text-surface-foreground">
                                    {s("h1_line1")} <br />
                                    <span className="text-primary italic">{s("h1_accent")}</span>
                                </h1>
                            </motion.div>
                        </div>
                        <motion.p className="text-xl text-surface-foreground/70 max-w-2xl leading-relaxed border-l-2 border-primary pl-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}>
                            <span dangerouslySetInnerHTML={{ __html: s("subtitle") }} />
                        </motion.p>
                    </div>
                </section>

                {/* Split Content Section */}
                <main className="flex-1 pb-32">
                    <div className="mx-auto max-w-7xl px-6 mt-[-3rem] relative z-20">
                        <div className="grid lg:grid-cols-[1fr_400px] gap-12 lg:gap-16 items-start">

                            {/* Main Reading Flow */}
                            <Reveal direction="left">
                                <div className="bg-white border border-border p-6 sm:p-8 md:p-14 shadow-2xl">
                                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-black leading-[1.1] tracking-tight mb-6 sm:mb-10 flex flex-col gap-2">
                                        {s("main_title")}
                                        <span className="text-foreground/30 italic">{s("main_title_accent")}</span>
                                    </h2>
                                    <p className="text-base sm:text-lg leading-relaxed text-foreground/70 mb-6 sm:mb-8">
                                        <span dangerouslySetInnerHTML={{ __html: s("p1") }} />
                                    </p>
                                    <p className="text-base sm:text-lg leading-relaxed text-foreground/70 mb-8 sm:mb-12">
                                        <span dangerouslySetInnerHTML={{ __html: s("p2") }} />
                                    </p>

                                    <hr className="border-border my-12" />

                                    <h3 className="font-display text-xl sm:text-2xl font-black uppercase mb-6 sm:mb-8 flex items-center gap-3">
                                        <span className="w-8 h-1 bg-primary inline-block"></span> {s("types_title")}
                                    </h3>

                                    <div className="space-y-4 sm:space-y-6">
                                        {types.map((cap, i) => {
                                            const icons = [Droplets, Factory, ShieldCheck];
                                            const Icon = icons[i % icons.length];
                                            return (
                                                <div key={i} className="group relative overflow-hidden bg-surface-foreground/5 border-l-4 border-surface p-6 transition-all hover:bg-surface hover:border-primary">
                                                    <div className="relative z-10 flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
                                                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-surface group-hover:text-primary-foreground" strokeWidth={1.5} />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-lg sm:text-xl font-bold uppercase tracking-wide text-foreground group-hover:text-surface-foreground transition-colors mb-3 sm:mb-4">{cap.title}</h4>
                                                            <ul className="space-y-1.5 sm:space-y-2">
                                                                {cap.items.map((item) => (
                                                                    <li key={item} className="flex items-start gap-2 text-xs sm:text-sm font-medium text-foreground/70 group-hover:text-surface-foreground/80">
                                                                        <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary shrink-0 mt-0.5 sm:mt-0" strokeWidth={2.5} />
                                                                        <span>{item}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Reveal>

                            {/* Sticky Sidebar */}
                            <Reveal direction="right" delay={0.2}>
                                <aside className="lg:sticky top-32 space-y-6 sm:space-y-8">

                                    {/* Key Stats Block */}
                                    <div className="bg-surface text-surface-foreground p-6 sm:p-8">
                                        <h3 className="font-display font-black text-base sm:text-lg uppercase mb-4 sm:mb-6 tracking-widest text-primary border-b border-surface-foreground/10 pb-3 sm:pb-4">{s("sidebar_title")}</h3>
                                        <div className="space-y-4 sm:space-y-6">
                                            {stats.map((stat) => (
                                                <div key={stat.label}>
                                                    <div className="font-display text-3xl sm:text-4xl font-black">{stat.n}<span className="text-lg sm:text-xl ml-1 text-primary">{stat.u}</span></div>
                                                    <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-widest text-surface-foreground/50 font-semibold">{stat.label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Equipment Capabilities */}
                                    <div className="border border-border p-6 sm:p-8 bg-surface-foreground/5">
                                        <h3 className="font-display font-black text-base sm:text-lg uppercase mb-4 sm:mb-6 tracking-widest text-foreground">{s("equipment_title")}</h3>
                                        <ul className="space-y-4 sm:space-y-6">
                                            {equipment.map((eq, i) => {
                                                const eqIcons = [HardHat, ShieldCheck];
                                                const Icon = eqIcons[i % eqIcons.length];
                                                return (
                                                    <li key={i} className="flex gap-3 sm:gap-4">
                                                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0 opacity-80" strokeWidth={1.5} />
                                                        <div>
                                                            <h4 className="font-bold text-xs sm:text-sm uppercase">{eq.title}</h4>
                                                            <p className="text-[10px] sm:text-xs text-foreground/60 mt-1">{eq.desc}</p>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <a href="/equipements" className="mt-6 sm:mt-8 flex items-center justify-center gap-2 w-full border border-surface text-surface py-2.5 sm:py-3 text-[10px] sm:text-xs font-bold uppercase tracking-widest hover:bg-surface hover:text-surface-foreground transition-colors">
                                            {s("fleet_cta") || "Voir les équipements"}
                                        </a>
                                    </div>

                                    <a href="/contact" className="group flex flex-col justify-between bg-primary p-6 sm:p-8 text-primary-foreground min-h-[180px] sm:min-h-[200px] hover:bg-primary/95 transition-colors">
                                        <ArrowUpRight className="w-6 h-6 sm:w-8 sm:h-8 opacity-50 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                                        <div>
                                            <h4 className="font-display font-black text-xl sm:text-2xl uppercase mt-3 sm:mt-4 mb-2" dangerouslySetInnerHTML={{ __html: s("cta_title") }} />
                                            <p className="text-xs sm:text-sm font-medium opacity-80 uppercase tracking-widest">Dimensionnement et Coûts</p>
                                        </div>
                                    </a>

                                </aside>
                            </Reveal>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
}
